import { lazy, useEffect } from "react";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import { DeveloperToolsDialog } from "@/components/dev-tools/DeveloperToolsDialog";
import { UpdatedPrivacyPolicyAcceptanceModal } from "@/components/legal/UpdatedPrivacyPolicyAcceptanceModal";
import { UpdatedTermsAcceptanceModal } from "@/components/legal/UpdatedTermsAcceptanceModal";
import { UpdatedTermsAndPrivacyPolicyAcceptanceModal } from "@/components/legal/UpdatedTermsAndPrivacyPolicyAcceptanceModal";
import { BridgeToDesktopPage } from "@/components/onboarding/BridgeToDesktopPage";
import { useInitializeApp } from "@/hooks/app/useInitializeApp";
import { useCheckAppPath } from "@/hooks/tauri/useMoveAppToFolder";
import { useIntl } from "@/infra/locale";
import { AppRoutes } from "@/infra/router";
import GuestRoute from "@/infra/router/GuestRoute";
import LazyPage from "@/infra/router/LazyPage";
import ProtectedRoute from "@/infra/router/ProtectedRoute";
import { ConnectionStatusPage } from "@/pages/ConnectionStatusPage";
import { HomePage } from "@/pages/HomePage";
import { PhysiotherapistDisconnectedGuard, PrescriptionExpiredGuard } from "@/pages/InvalidPatientStatusGuard";
import { RootPage } from "@/pages/RootPage";
import { dashboardRoutes } from "@/pages/dashboard/routes";
import { legalRoutes } from "@/pages/legal/routes";
import { GUEST_ONBOARDING_ROUTES, PROTECTED_ONBOARDING_ROUTES } from "@/pages/onboarding/routes";
import { sessionRoutes, selfReflectionRoutes } from "@/pages/sessions/routes";
import { settingsRoutes } from "@/pages/settings/routes";
import { getAuthOS } from "@/utils/getAuthOS";
import { careEnvironment } from "@akina/config";
import { useAuth } from "@akina/infra";
import {
  LoadingOverlay,
  useScreenSizeDetector,
  Button,
  UseOnLargerScreenModal,
  NotFoundPage,
} from "@akina/presentation";
import { initLogMetrics } from "@akina/utils/shouldLogMetrics";
import { akinaAsciiArt } from "../asciiArt";

const SettingsLayout = lazy(() => import("@/components/settings/SettingsLayout"));
const NavigationBarLayout = lazy(() => import("@/components/navigation/NavigationBarLayout"));
const SelfReflectionBaseLayout = lazy(() => import("@/components/self-reflection/layouts/SelfReflectionBaseLayout"));

const isOnLegalPage = (pathname: AppRoutes) =>
  pathname === AppRoutes.PrivacyPolicyAndDataTransparency || pathname === AppRoutes.TermsAndConditions;

const isOnOnboardingPage = (pathname: AppRoutes) =>
  pathname === AppRoutes.OnboardingVerify ||
  pathname === AppRoutes.OnboardingTermsAndConditions ||
  pathname === AppRoutes.Onboarding;

const Login = () => {
  const { loginWithRedirect } = useAuth();

  useEffect(() => {
    const login = async () => {
      const os = await getAuthOS();
      loginWithRedirect({ returnTo: AppRoutes.Dashboard }, "", os);
    };
    login();
  }, [loginWithRedirect]);

  return null;
};

akinaAsciiArt();

const App = () => {
  const {
    isInitializing,
    patientTerms,
    termsModalOpen,
    setTermsModalOpen,
    privacyPolicyModalOpen,
    setPrivacyPolicyModalOpen,
    termsAndPrivacyPolicyModalOpen,
    setTermsAndPrivacyPolicyModalOpen,
  } = useInitializeApp();
  const pathname = useLocation().pathname as AppRoutes;
  const { isMobile } = useScreenSizeDetector();
  const { formatMessage } = useIntl();

  useCheckAppPath();

  useEffect(() => {
    initLogMetrics();
  }, []);

  return (
    <>
      {isMobile && !isOnOnboardingPage(pathname) ? <UseOnLargerScreenModal formatMessage={formatMessage} /> : null}
      {patientTerms && !isOnLegalPage(pathname) && !isOnOnboardingPage(pathname) ? (
        <UpdatedTermsAcceptanceModal
          patientTerms={patientTerms}
          open={termsModalOpen}
          onOpenChange={() => setTermsModalOpen(!termsModalOpen)}
        />
      ) : null}
      {patientTerms && !isOnLegalPage(pathname) && !isOnOnboardingPage(pathname) ? (
        <UpdatedPrivacyPolicyAcceptanceModal
          patientTerms={patientTerms}
          open={privacyPolicyModalOpen}
          onOpenChange={() => setPrivacyPolicyModalOpen(!privacyPolicyModalOpen)}
        />
      ) : null}
      {patientTerms && !isOnLegalPage(pathname) && !isOnOnboardingPage(pathname) ? (
        <UpdatedTermsAndPrivacyPolicyAcceptanceModal
          open={termsAndPrivacyPolicyModalOpen}
          onOpenChange={() => setTermsAndPrivacyPolicyModalOpen(!termsAndPrivacyPolicyModalOpen)}
        />
      ) : null}

      {isInitializing ? <LoadingOverlay /> : null}
      {careEnvironment.showDeveloperTools ? <DeveloperToolsDialog /> : null}
      <Routes>
        <Route element={<ConnectionStatusPage />}>
          {legalRoutes}
          <Route path="/" element={<RootPage />} />
          <Route path={AppRoutes.BridgeToDesktop} element={<BridgeToDesktopPage />} />
          <Route element={<GuestRoute />}>
            <Route path={AppRoutes.Login} element={<Login />} />
            {GUEST_ONBOARDING_ROUTES}
          </Route>

          <Route path={AppRoutes.Home} element={<HomePage />} />
          <Route element={<ProtectedRoute />}>
            {PROTECTED_ONBOARDING_ROUTES}
            <Route
              key={AppRoutes.PrescriptionExpired}
              path={AppRoutes.PrescriptionExpired}
              element={<LazyPage element={<PrescriptionExpiredGuard />} />}
            />

            <Route
              key={AppRoutes.PatientDisconnected}
              path={AppRoutes.PatientDisconnected}
              element={<LazyPage element={<PhysiotherapistDisconnectedGuard />} />}
            />

            <Route element={<LazyPage element={<NavigationBarLayout />} />}>
              {dashboardRoutes}
              <Route element={<LazyPage element={<SettingsLayout />} />}>{settingsRoutes}</Route>
            </Route>
            <Route element={<LazyPage element={<SelfReflectionBaseLayout />} />}>{selfReflectionRoutes}</Route>
            {sessionRoutes}
          </Route>

          <Route
            path={"*"}
            element={
              <NotFoundPage
                button={
                  <Link to={AppRoutes.DashboardToday}>
                    <Button>Go back to home</Button>
                  </Link>
                }
              />
            }
          />
        </Route>
      </Routes>
    </>
  );
};

export default App;
